/**
 * ChatThread — unified scroll container rendering MessageList + ChatComposer.
 *
 * Handles both 1:1 interview and focus group modes in a single component.
 * Auto-scrolls to bottom on mount and when new messages arrive.
 * Shows ClosedRoomBanner when the room is closed instead of the composer.
 */

import { ChatScrollContainer } from '@/components/chat/ChatScrollContainer'
import { ConversationEmptyState } from '@/components/chat/ConversationEmptyState/ConversationEmptyState'
import { ConversationStage } from '@/components/chat/ConversationStage/ConversationStage'
import { ParticipantPopover } from '../ChatSidebar/ParticipantPopover'
import { ClosedRoomBanner } from './ClosedRoomBanner'
import { MessageList } from './MessageList'
import { ChatComposer, RoomComposerBinding } from './ChatComposer'
import { QueuedMessageBar } from './QueuedMessageBar'
import { TrialMessageLimitNotice } from '@/components/billing/TrialMessageLimitNotice'
import type { ChatImage } from '@/lib/api'
import type { QueueBlockedReason, QueuedMessage } from '../useChatRoom'

import {
  ChatRoomMessageDto,
  ChatRoomParticipantDto,
  ChatRoomReactionDto,
  ChatRoomStatus,
} from '@digital-twins/shared-types'

interface ChatThreadProps {
  /** Height in px of a header floating over the top of the thread (see ChatScrollContainer). */
  topInset?: number
  messages: Array<ChatRoomMessageDto>
  participants: Array<ChatRoomParticipantDto>
  roomStatus: ChatRoomStatus
  onSend: (content: string, images?: Array<ChatImage>, captureUrl?: string, stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>) => void
  thinkingParticipantIds?: Array<string>
  isSending?: boolean
  isWaitingForTurn?: boolean
  /** Why the queue cannot drain, straight from useChatRoom. Null means it can. */
  queueBlockedReason?: QueueBlockedReason | null
  messageQueue?: Array<QueuedMessage>
  onQueueEdit?: (id: string, content: string) => void
  onQueueRemove?: (id: string) => void
  onQueueRetry?: (id: string) => void
  blockedQueuedId?: string | null
  sendError?: string | null
  placeholder?: string
  emptyStateText?: string
  /**
   * Prompt for an empty conversation. When set, an empty thread shows who is in
   * the room with the composer centered under it, and the composer shows this
   * prompt as its placeholder until the first message; without
   * it (or with a composerOverride, closed room or trial cap) the thread keeps
   * the plain emptyStateText line and the bottom composer.
   */
  emptyHeadline?: string
  /** Lets the empty-state profile cards load the richer featured-twin profile. */
  populationId?: string | null
  /** When true (trial cap reached), the composer is replaced by an upgrade notice. */
  atMessageLimit?: boolean
  isRoomActive?: boolean
  /** When set, renders a "Guest conversation" divider before the message at this index. */
  guestDividerIndex?: number
  /** Replaces the entire composer with a custom node (e.g. locked guest composer). */
  composerOverride?: React.ReactNode
  messagesError?: boolean
  onRetryMessages?: () => void
  /** Room the messages belong to; required outside the `/chats/:id` route. */
  roomToken?: string
  /** Resolves stored image keys to URLs (guests use the public share route). */
  getImageUrl?: (token: string, key: string) => string
  /** C13: the room's resolved reactions, rendered as chips under messages. */
  reactions?: Array<ChatRoomReactionDto>
  /** Keys of reactions that arrived live; only those chips pop. */
  liveReactionKeys?: ReadonlySet<string>
  /** Room page only (Step 11b/11c): teller picks, @tag sync, PDF documents, all-off block. Guests never pass it. */
  roomComposer?: RoomComposerBinding
  /**
   * The room's suggested opening question. Shown in the centered composer only while the
   * conversation is empty, ready to send; guests never pass it.
   */
  openingQuestion?: string | null
  /**
   * A failed room settings save (e.g. the composer's "Speaking first" stepper), shown
   * above the composer next to the send error. The page passes it while the pane is closed.
   */
  settingsError?: string | null
}

export function ChatThread(props: ChatThreadProps) {
  const {
    messages,
    participants,
    roomStatus,
    onSend,
    thinkingParticipantIds = [],
    isSending = false,
    isWaitingForTurn = false,
    queueBlockedReason = null,
    messageQueue = [],
    onQueueEdit,
    onQueueRemove,
    onQueueRetry,
    blockedQueuedId,
    sendError,
    placeholder,
    emptyStateText,
    emptyHeadline,
    populationId = null,
    atMessageLimit = false,
    isRoomActive = false,
    guestDividerIndex,
    composerOverride,
    messagesError = false,
    onRetryMessages,
    roomToken,
    getImageUrl,
    reactions,
    liveReactionKeys,
    roomComposer,
    openingQuestion = null,
    settingsError = null,
    topInset = 0,
  } = props

  const isClosed = roomStatus === 'closed'
  // Reactions count too, so a chip landing on the last message stays in view.
  const scrollKey = messages.length + thinkingParticipantIds.length + (reactions?.length ?? 0)
  const isEmpty = messages.length === 0 && thinkingParticipantIds.length === 0 && messageQueue.length === 0
  const isCenteredEmpty =
    Boolean(emptyHeadline) && isEmpty && !messagesError && !composerOverride && !isClosed && !atMessageLimit

  const sendErrorLine =
    sendError || settingsError ? (
      <>
        {settingsError && (
          <p role="alert" data-testid="chat-settings-error" className="mx-4 mb-2 text-sm text-destructive">
            {settingsError}
          </p>
        )}
        {sendError && (
          <p role="alert" data-testid="chat-send-error" className="mx-4 mb-2 text-sm text-destructive">
            {sendError}
          </p>
        )}
      </>
    ) : null

  const emptyHero = (
    <ConversationEmptyState
      twins={participants.map((participant) => ({
        id: participant.personaId,
        name: participant.displayName,
        imageUrl: participant.imageUrl,
        segmentNames: participant.audienceTagNames,
      }))}
      headline={emptyHeadline ?? ''}
      renderProfile={(twinIndex, card) => (
        <ParticipantPopover participant={participants[twinIndex]} populationId={populationId} hidePhoto>
          {card}
        </ParticipantPopover>
      )}
    />
  )

  const thread = (
    <ChatScrollContainer
      scrollKey={scrollKey}
      className="flex-1 min-h-0 px-4 py-4 space-y-4"
      hasTopFade
      topInset={topInset}
      data-testid="chat-thread-messages"
    >
      <MessageList
        messages={messages}
        participants={participants}
        thinkingParticipantIds={thinkingParticipantIds}
        isRoomActive={isRoomActive}
        emptyStateText={emptyStateText}
        guestDividerIndex={guestDividerIndex}
        roomToken={roomToken}
        getImageUrl={getImageUrl}
        reactions={reactions}
        liveReactionKeys={liveReactionKeys}
      />
    </ChatScrollContainer>
  )

  const composer = composerOverride ? (
    composerOverride
  ) : isClosed ? (
    <ClosedRoomBanner />
  ) : atMessageLimit ? (
    <TrialMessageLimitNotice />
  ) : (
    <>
      {/* A queued message is by definition not sent yet, so it is always
          shown. The previous condition required either a second queued
          message or a 409, which silently hid the user's own typed
          question during an ordinary turn. */}
      {messageQueue.length > 0 && onQueueEdit && onQueueRemove && (
        <QueuedMessageBar
          queue={messageQueue}
          isSending={isSending}
          blockedReason={
            queueBlockedReason ?? (isWaitingForTurn ? 'room-busy' : null)
          }
          onEdit={onQueueEdit}
          onRemove={onQueueRemove}
          onRetry={onQueueRetry}
          blockedQueuedId={blockedQueuedId}
        />
      )}
      {/* One composer for the whole conversation: it rests mid-page in its hero
          look while the room is empty and glides down with the first message. */}
      <ChatComposer
        onSend={onSend}
        roomStatus={roomStatus}
        placeholder={isCenteredEmpty ? emptyHeadline : placeholder}
        participants={participants}
        roomComposer={roomComposer}
        hero={isCenteredEmpty}
        suggestion={isCenteredEmpty ? openingQuestion : null}
      />
    </>
  )

  return (
    <div
      className="flex flex-1 flex-col min-h-0"
      // The column is pulled up under a floating header; only the thread scroller
      // wants that overlap, so every other state steps back below the header.
      style={topInset > 0 && (isCenteredEmpty || messagesError) ? { paddingTop: topInset } : undefined}
      data-testid="chat-thread"
    >
      {messagesError && (
        <div role="alert" data-testid="chat-history-error" className="flex shrink-0 flex-col items-center justify-center gap-3 px-4 py-3 text-center">
          <p className="text-sm text-twin-chat-fg-muted">
            {messages.length === 0 ? "Messages couldn't be loaded." : 'Some messages may be missing. Retry loading the conversation.'}
          </p>
          {onRetryMessages && (
            <button
              type="button"
              onClick={onRetryMessages}
              className="rounded-md bg-twin-chat-bubble/20 px-4 py-2 text-sm font-medium text-twin-chat-fg hover:bg-twin-chat-bubble/30 transition-colors"
              data-testid="retry-messages-btn"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {(!messagesError || messages.length > 0) && (
        <ConversationStage
          isEmpty={isCenteredEmpty}
          emptyHero={emptyHero}
          thread={thread}
          beforeComposer={sendErrorLine}
          composer={composer}
        />
      )}
    </div>
  )
}
